import * as React from "react";
import {
  TimeField as AriaTimeField,
  DateInput,
  DateSegment,
  I18nProvider,
  Label,
  ListBox,
  ListBoxItem,
} from "react-aria-components";
import { Time, parseTime } from "@internationalized/date";
import { ChevronDown as ChevronDownIcon, Clock as ClockIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/primitives/popover";

type Props = {
  /** "HH:mm" — same string format the form schema already uses. Empty = unset. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  ariaLabel?: string;
};

const MINUTES_PER_QUARTER_HOUR = 15;
const QUARTER_HOURS_PER_DAY = (24 * 60) / MINUTES_PER_QUARTER_HOUR;

function stringToTime(value: string): Time | null {
  if (!value) return null;
  try {
    return parseTime(value.length === 5 ? `${value}:00` : value);
  } catch {
    return null;
  }
}

function timeToString(time: Time | null): string {
  if (!time) return "";
  const hh = time.hour.toString().padStart(2, "0");
  const mm = time.minute.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

const quarterHours: string[] = Array.from({ length: QUARTER_HOURS_PER_DAY }, (_, step) => {
  const minutesFromMidnight = step * MINUTES_PER_QUARTER_HOUR;
  return timeToString(
    new Time(Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60),
  );
});

function nextQuarterHour(now: Date): string {
  const minutesFromMidnight = now.getHours() * 60 + now.getMinutes();
  const step =
    Math.ceil(minutesFromMidnight / MINUTES_PER_QUARTER_HOUR) % QUARTER_HOURS_PER_DAY;
  return quarterHours[step];
}

/**
 * LGo time picker — built on react-aria-components TimeField.
 * No vendor CSS imports (Bun-friendly). Tailwind-styled to match the
 * shadcn input shell. Emits the same `"HH:mm"` string the form schema
 * already expects.
 *
 * The segments stay typable for any minute: the list is a shortcut, not a constraint.
 */
export function TimePicker({
  value,
  onChange,
  disabled,
  id,
  className,
  ariaLabel = "Čas",
}: Props) {
  const [isListOpen, setIsListOpen] = React.useState(false);
  const [listNode, setListNode] = React.useState<HTMLDivElement | null>(null);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const focusedSegment = React.useRef<HTMLElement | null>(null);
  const listTakesFocus = React.useRef(false);
  const anchorTime = React.useRef("");
  const anchorOption = React.useRef<HTMLDivElement | null>(null);

  // The sheet around this field locks scrolling through react-remove-scroll, which
  // cancels every wheel and touchmove event reaching `document` from outside the
  // sheet's own subtree — and this list is portalled outside it.
  React.useEffect(() => {
    if (!listNode) return;
    const keepTheScrollOffTheScrollLock = (event: Event) => event.stopPropagation();
    listNode.addEventListener("wheel", keepTheScrollOffTheScrollLock);
    listNode.addEventListener("touchmove", keepTheScrollOffTheScrollLock);
    return () => {
      listNode.removeEventListener("wheel", keepTheScrollOffTheScrollLock);
      listNode.removeEventListener("touchmove", keepTheScrollOffTheScrollLock);
    };
  }, [listNode]);

  const aimTheListAtTheLikeliestTime = () => {
    anchorTime.current = value || nextQuarterHour(new Date());
    anchorOption.current = null;
  };

  // react-aria builds the options in a later render pass, so the anchor reports
  // itself through its own ref — an effect on the open state runs too early.
  const holdOntoTheAnchorOption = React.useCallback((option: HTMLDivElement | null) => {
    anchorOption.current = option;
  }, []);

  // Taking focus makes react-aria restore onto the list whatever scroll offset it last
  // recorded, which on a list this new is still zero. A reveal from the item's own ref
  // is therefore always overwritten; it has to come after that restore instead.
  const revealTheAnchorTime = () => {
    anchorOption.current?.scrollIntoView({ block: "center" });
  };

  const openTheListBesideTheCaret = () => {
    if (disabled) return;
    listTakesFocus.current = false;
    aimTheListAtTheLikeliestTime();
    setIsListOpen(true);
  };

  const handTheKeyboardToTheList = () => {
    listTakesFocus.current = true;
    aimTheListAtTheLikeliestTime();
  };

  const confirmTime = (time: string) => {
    if (time !== value) onChange(time);
    setIsListOpen(false);
    if (!listTakesFocus.current) focusedSegment.current?.focus();
  };

  return (
    // Czech locale regardless of browser language: 24h clock, "HH:mm".
    <I18nProvider locale="cs-CZ">
      <Popover open={isListOpen} onOpenChange={setIsListOpen}>
        <PopoverAnchor asChild>
          <div
            ref={fieldRef}
            className={cn(
              "flex items-center h-10 w-full rounded-md border border-input bg-background pr-1 text-sm",
              "transition-colors duration-fast ease-out-quart",
              "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
              disabled && "opacity-50 cursor-not-allowed",
              className,
            )}
          >
            <div
              className="flex h-full min-w-0 flex-1 items-center gap-1 pl-2.5"
              onClickCapture={openTheListBesideTheCaret}
              onFocusCapture={(event) => {
                focusedSegment.current = event.target;
              }}
            >
              <ClockIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <AriaTimeField
                value={stringToTime(value)}
                onChange={(next) => onChange(timeToString(next as Time | null))}
                isDisabled={disabled}
                hourCycle={24}
                shouldForceLeadingZeros
                granularity="minute"
                aria-label={ariaLabel}
                className="flex-1 min-w-0"
              >
                {id && <Label className="sr-only" htmlFor={id}>{ariaLabel}</Label>}
                <DateInput className="flex items-center gap-1 outline-none">
                  {(segment) => (
                    <DateSegment
                      segment={segment}
                      className={cn(
                        "tabular-nums caret-transparent rounded px-0.5 outline-none",
                        "data-[type=literal]:px-0 data-[type=literal]:text-muted-foreground",
                        "data-[placeholder]:text-muted-foreground",
                        "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                      )}
                    />
                  )}
                </DateInput>
              </AriaTimeField>
            </div>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={`${ariaLabel}: vybrat ze seznamu`}
                onClick={handTheKeyboardToTheList}
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
              >
                <ChevronDownIcon />
              </Button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-28 p-1"
          // The sheet around this field traps focus and pulls it back out of anything
          // portalled beside it; only once the popover's own focus scope has mounted
          // is that trap paused, and this event is the first moment after that.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (listTakesFocus.current) anchorOption.current?.focus();
            revealTheAnchorTime();
          }}
          onCloseAutoFocus={(event) => {
            if (!listTakesFocus.current) event.preventDefault();
          }}
          // Working the field while the list is open is the point of opening it
          // from the field, so a click or a focus move inside it must not dismiss.
          onInteractOutside={(event) => {
            if (fieldRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
        >
          <ListBox
            ref={setListNode}
            aria-label={ariaLabel}
            selectionMode="single"
            selectedKeys={value ? [value] : []}
            onSelectionChange={(keys) => {
              const [picked] = Array.from(keys);
              // Re-picking the time the field already holds clears the selection
              // instead of reporting it again — the value stands, the list closes.
              confirmTime(picked == null ? value : String(picked));
            }}
            className="max-h-60 overflow-y-auto outline-none"
          >
            {quarterHours.map((time) => (
              <ListBoxItem
                key={time}
                id={time}
                ref={time === anchorTime.current ? holdOntoTheAnchorOption : null}
                className={cn(
                  "cursor-pointer select-none rounded-sm px-2 py-1.5 text-sm tabular-nums outline-none",
                  "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                  "data-[selected]:font-medium data-[selected]:text-primary",
                )}
              >
                {time}
              </ListBoxItem>
            ))}
          </ListBox>
        </PopoverContent>
      </Popover>
    </I18nProvider>
  );
}
